import { Injectable, NgZone, inject } from '@angular/core';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class ChunkLoadRecoveryService {
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly zone = inject(NgZone);

  private readonly reloadAttemptKey = 'tds_chunk_reload_attempted';
  private readonly reloadGraceMs = 15_000;
  private readonly deploymentReadinessAttempts = 10;
  private readonly deploymentReadinessRetryMs = 1_500;
  private readonly chunkLoadPattern =
    /Failed to fetch dynamically imported module|Importing a module script failed|Failed to load module script|Expected a JavaScript-or-Wasm module script|MIME type of ["']text\/html|ChunkLoadError|Loading chunk [\w-]+ failed/i;

  private initialized = false;
  private isVersionMessageVisible = false;
  private isWaitingForDeployment = false;

  init(): void {
    if (this.initialized || typeof window === 'undefined') {
      return;
    }

    this.initialized = true;
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);

    // If the app remains stable for a while, allow a future chunk failure to
    // trigger one automatic reload again in this tab.
    window.setTimeout(() => {
      sessionStorage.removeItem(this.reloadAttemptKey);
    }, this.reloadGraceMs);
  }

  handle(error: unknown): boolean {
    return this.processChunkLoadFailure(error);
  }

  private readonly handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
    if (this.processChunkLoadFailure(event.reason)) {
      event.preventDefault();
    }
  };

  private processChunkLoadFailure(error: unknown): boolean {
    const message = this.extractErrorMessage(error);
    if (!this.chunkLoadPattern.test(message)) {
      return false;
    }

    this.zone.run(() => this.recoverFromChunkFailure());
    return true;
  }

  private recoverFromChunkFailure(): void {
    if (this.isWaitingForDeployment) {
      return;
    }

    const hasReloaded = sessionStorage.getItem(this.reloadAttemptKey) === '1';
    if (!hasReloaded) {
      sessionStorage.setItem(this.reloadAttemptKey, '1');
      void this.reloadWhenDeploymentIsReady();
      return;
    }

    this.showNewVersionMessage();
  }

  private showNewVersionMessage(): void {
    if (this.isVersionMessageVisible) {
      return;
    }

    this.isVersionMessageVisible = true;
    const horizontalPosition: MatSnackBarHorizontalPosition = 'end';
    const verticalPosition: MatSnackBarVerticalPosition = 'top';

    this.snackBar
      .open(
        this.translate.instant('common.newVersionAvailable'),
        this.translate.instant('common.reload'),
        {
          duration: 0,
          horizontalPosition,
          verticalPosition,
          panelClass: 'snackbar-version-update',
        }
      )
      .onAction()
      .subscribe(() => {
        this.isVersionMessageVisible = false;
        void this.reloadWhenDeploymentIsReady();
      });
  }

  private async reloadWhenDeploymentIsReady(): Promise<void> {
    if (this.isWaitingForDeployment) {
      return;
    }

    this.isWaitingForDeployment = true;
    this.snackBar.dismiss();
    const preparingMessage = this.snackBar.open(
      this.translate.instant('common.preparingNewVersion'),
      '',
      {
        duration: 0,
        horizontalPosition: 'end',
        verticalPosition: 'top',
        panelClass: 'snackbar-version-update',
      }
    );

    try {
      for (let attempt = 0; attempt < this.deploymentReadinessAttempts; attempt++) {
        if (await this.isDeploymentReady()) {
          this.reloadWithCacheBust();
          return;
        }

        await this.wait(this.deploymentReadinessRetryMs);
      }
    } catch (error) {
      console.warn('Could not verify that the new frontend deployment is ready.', error);
    }

    preparingMessage.dismiss();
    this.isWaitingForDeployment = false;
    this.isVersionMessageVisible = false;
    this.showNewVersionMessage();
  }

  private async isDeploymentReady(): Promise<boolean> {
    const documentUrl = this.buildCacheBustedUrl(window.location.href);
    const documentResponse = await fetch(documentUrl, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      },
    });
    const documentContentType = documentResponse.headers.get('content-type') || '';
    if (!documentResponse.ok || !documentContentType.toLowerCase().includes('text/html')) {
      return false;
    }

    const html = await documentResponse.text();
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const documentBaseUrl = documentResponse.url || documentUrl;
    const configuredBaseHref = parsedDocument.querySelector<HTMLBaseElement>('base[href]')?.getAttribute('href');
    const assetBaseUrl = configuredBaseHref
      ? new URL(configuredBaseHref, documentBaseUrl).toString()
      : documentBaseUrl;
    const scriptUrls = Array.from(parsedDocument.querySelectorAll<HTMLScriptElement>('script[src]'))
      .map((element) => this.resolveSameOriginUrl(element.getAttribute('src'), assetBaseUrl))
      .filter((url): url is string => !!url);
    const preloadUrls = Array.from(parsedDocument.querySelectorAll<HTMLLinkElement>('link[rel="modulepreload"][href]'))
      .map((element) => this.resolveSameOriginUrl(element.getAttribute('href'), assetBaseUrl))
      .filter((url): url is string => !!url);

    if (!scriptUrls.length) {
      return false;
    }

    const scriptBodies = await Promise.all(
      scriptUrls.map((url) => this.fetchJavaScriptAsset(url, true))
    );
    if (scriptBodies.some((body) => body === null)) {
      return false;
    }

    const preloadsReady = await Promise.all(
      preloadUrls.map((url) => this.fetchJavaScriptAsset(url, false))
    );
    if (preloadsReady.some((body) => body === null)) {
      return false;
    }

    const lazyChunkUrls = new Set<string>();
    const dynamicImportPattern = /import\(\s*["']([^"']+\.(?:m?js)(?:\?[^"']*)?)["']\s*\)/g;
    for (const body of scriptBodies) {
      if (!body) {
        continue;
      }

      let match: RegExpExecArray | null;
      while ((match = dynamicImportPattern.exec(body)) !== null) {
        const url = this.resolveSameOriginUrl(match[1], assetBaseUrl);
        if (url) {
          lazyChunkUrls.add(url);
        }
      }
    }

    const lazyChunksReady = await Promise.all(
      Array.from(lazyChunkUrls).map((url) => this.fetchJavaScriptAsset(url, false))
    );
    return lazyChunksReady.every((body) => body !== null);
  }

  private async fetchJavaScriptAsset(url: string, readBody: boolean): Promise<string | null> {
    try {
      const response = await fetch(url, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
      const contentType = (response.headers.get('content-type') || '').toLowerCase();
      const isJavaScript = contentType.includes('javascript')
        || contentType.includes('ecmascript')
        || contentType.includes('wasm');
      if (!response.ok || !isJavaScript) {
        await response.body?.cancel().catch(() => undefined);
        return null;
      }

      if (readBody) {
        return response.text();
      }

      await response.body?.cancel().catch(() => undefined);
      return '';
    } catch {
      return null;
    }
  }

  private resolveSameOriginUrl(value: string | null, baseUrl: string): string | null {
    if (!value) {
      return null;
    }

    try {
      const url = new URL(value, baseUrl);
      return url.origin === window.location.origin ? url.toString() : null;
    } catch {
      return null;
    }
  }

  private buildCacheBustedUrl(value: string): string {
    const url = new URL(value);
    url.searchParams.set('__tds_reload', Date.now().toString());
    return url.toString();
  }

  private reloadWithCacheBust(): void {
    const url = new URL(window.location.href);
    url.searchParams.set('__tds_reload', Date.now().toString());
    window.location.replace(url.toString());
  }

  private wait(milliseconds: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  }

  private extractErrorMessage(error: unknown): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error instanceof Error) {
      return `${error.name}: ${error.message}`;
    }

    if (error && typeof error === 'object') {
      const message = (error as { message?: unknown }).message;
      if (typeof message === 'string') {
        return message;
      }
    }

    return '';
  }
}
